import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import config from 'config';

import { getReduxStore } from 'store/storeHolder';
import {
  resetForm as resetBookingForm,
  setStep
} from 'store/reducers/bookingAppointmentSlice';
import { resetForm as resetGiftCardForm } from 'store/reducers/giftCardsShopSlice';
import { guestSessionFinished } from 'store/reducers/guestSlice';
import { resetForm as resetMembershipForm } from 'store/reducers/membershipsSlice';
import { setUserInfo } from 'store/reducers/persistAuthSlice';
import { clearGuestInfo } from 'store/reducers/persistGuestAuthSlice';
import { resetServicesState } from 'store/reducers/servicesSlice';
import {
  authSessionFinished,
  setIsLoading,
  setRedirectFlow
} from 'store/reducers/userSlice';

import { clearPostAuthReturnUrl } from 'utils/postAuthReturnUrl';

import { BookingSteps } from 'types';

const { url, liveUrl } = config.api;

const baseUrl = process.env.APP_ENV === 'production' ? liveUrl : url;

export const instance = Axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  responseType: 'json',
  timeout: 60000
});

instance.interceptors.request.use(
  async (axiosConfig: InternalAxiosRequestConfig) => {
    const store = getReduxStore();
    const { userInfo } = store.getState().persistAuth;
    const client_ip = localStorage.getItem('client_ip');
    const { guestInfo } = store.getState().persistGuestAuth;

    axiosConfig.params = {
      ...axiosConfig.params,
      vsgapp: 3001
    };

    if (client_ip) {
      axiosConfig.headers['client-ip'] = client_ip;
    }

    if (userInfo) {
      axiosConfig.params = {
        ...axiosConfig.params,
        clid: userInfo?.clid,
        token: userInfo?.token
      };
    } else if (guestInfo) {
      const isCartEndpoint = axiosConfig.url?.toLowerCase().includes('cart');
      const isInvoiceId = isCartEndpoint && guestInfo.invoice_id;
      axiosConfig.params = {
        ...axiosConfig.params,
        clid: guestInfo?.clid,
        token: guestInfo?.token,
        ...(isInvoiceId ? { invoice_id: guestInfo.invoice_id } : {})
      };
    }

    return axiosConfig;
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const errorStatus = error?.response?.status;
    if (errorStatus === 403) {
      const store = getReduxStore();
      const { userInfo } = store.getState().persistAuth;
      const { guestInfo } = store.getState().persistGuestAuth;

      if (userInfo !== null) {
        store.dispatch(authSessionFinished());
        store.dispatch(setUserInfo(null));
      }
      if (guestInfo !== null) {
        store.dispatch(guestSessionFinished());
        store.dispatch(clearGuestInfo());
      }

      store.dispatch(setIsLoading(false));
      store.dispatch(setStep(BookingSteps.SERVICE));
      store.dispatch(resetBookingForm());
      store.dispatch(resetGiftCardForm());
      store.dispatch(resetServicesState());
      store.dispatch(setRedirectFlow(null));
      clearPostAuthReturnUrl();
      store.dispatch(resetMembershipForm());
      const [
        { addressesApi },
        { shoppingCartApi },
        { userProfileApi },
        { paymentMethodsApi }
      ] = await Promise.all([
        import('../services/addressesApi'),
        import('../services/shoppingCartApi'),
        import('../services/userProfileApi'),
        import('../services/paymentMethodsApi')
      ]);
      store.dispatch(addressesApi.util.resetApiState());
      store.dispatch(shoppingCartApi.util.resetApiState());
      store.dispatch(userProfileApi.util.resetApiState());
      store.dispatch(paymentMethodsApi.util.resetApiState());
    }

    if (error?.response?.status === 500) {
      return Promise.reject(
        new Error('Something went wrong. Please try again.')
      );
    }

    return Promise.reject(error);
  }
);

export const post = (
  meth: string,
  data = {},
  options = {}
): Promise<AxiosResponse> => instance.post(meth, data, options);

export const get = (meth: string, params = {}): Promise<AxiosResponse> =>
  instance.get(meth, { params });

export const del = (
  meth: string,
  params = {},
  options = {}
): Promise<AxiosResponse> => instance.delete(meth, { params, ...options });
